import React from 'react';
import PropTypes from 'prop-types';

import Browser from '@wetransfer/concorde-browser';
import Cookie from '@wetransfer/concorde-cookie';

import { connect } from 'react-redux';

import { AppConfig } from '../../../core-bundle/config/app-config';

import UpsellCollectHeader from './upsell-collect';

import Router from '@module/router';
import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import { canBeShownAsGrid } from '@app/transfer-bundle/helpers/preview';

import Actions from '@app/store/action';
import Grid from '@app/page/transfer/grid';
import PreviewHelper from '@app/helper/preview';
import Store from '@app/store';
import { getCurrentSize } from '@app/transfer-bundle/store/selectors';

import Icon from '@component/icon';
import ItemList from '@component/itemlist';
import Scrollable from '@component/scrollable';
import GridButton from '@component/transfer/downloader/grid-button';
import TransferLink from '@component/transfer-link';
import NotificationPositions from '@constant/notification-positions';
import TransferLinkSources from '@constant/transfer-link-sources';

import { TransferDownloadActions } from '@app/transfer-bundle/store/download-actions';

const MESSAGE_CUTOFF_POINT = Browser.isMobile ? 170 : 55;

export class Files extends React.Component {
  state = {
    isReady: false,
    textExpanded: false,
    showAllFilesOnMobile: false,
    transferLinkButtonTimerId: null,
    transferLinkButtonLabel: I18n.t('transfer_link.buttons.copy'),
  };

  constructor(props) {
    super(props);

    this.onDownloadAll = this.onDownloadAll.bind(this);
    this.onExpandText = this.onExpandText.bind(this);
    this.onFileDownload = this.onFileDownload.bind(this);
    this.onShowAllFiles = this.onShowAllFiles.bind(this);
    this.trackOpenInAppButton = this.trackOpenInAppButton.bind(this);
    this.trackDownloadAppButton = this.trackDownloadAppButton.bind(this);
  }

  componentDidMount() {
    const { route, session } = this.props;
    const onGrid = route.path.includes('/grid');
    if (onGrid || !this.previewable) {
      return;
    }

    if (Browser.isMobile) {
      this.mobileGridTimeout = setTimeout(
        () =>
          Router.navigateTo(
            PreviewHelper.getGridUrl({
              transferId: this.props.transfer.id,
              recipientId: this.props.recipient,
              secret: this.props.secret,
            })
          ),
        0
      ); // Async
    } else if (!Cookie.get('wt_discover_vrl') && session && session.tandc) {
      Cookie.set('wt_discover_vrl', 1, { days: 365 });
      this.pushTransferNotification = setTimeout(() => {
        Store.dispatch(
          Actions.Notification.pushTransfer({
            text: 'DiscoverVrlTooltip',
            align: '.downloader__grid-button',
          })
        );
      }, 1000);
    }

    if (!this.props.file_id) return;

    this.scrollToActiveFile();
  }

  componentWillUnmount() {
    const { route } = this.props;
    if (Browser.isMobile && route.path.includes('/grid')) {
      const downloadUrl = route.path.split('/grid')[0];
      Router.navigateTo(downloadUrl);
    }
    clearTimeout(this.mobileGridTimeout);
    clearTimeout(this.pushTransferNotification);
  }

  get previewable() {
    return canBeShownAsGrid(this.props.transfer.itemsTree.items);
  }

  /**
   * If the file_id changes, call the scrollToActiveFile method
   *
   * @param {object} prevProps  Components' previous props
   */
  componentDidUpdate(prevProps) {
    if (prevProps.file_id !== this.props.file_id) {
      this.scrollToActiveFile();
    }
  }

  /**
   * If there is a mounted scrollable component, scroll to the active file.
   */
  scrollToActiveFile() {
    if (!this.node) {
      return;
    }

    const previewElem = this.node.querySelector('.filelist__item--previewing');

    if (!previewElem) {
      return;
    }

    if (this.scrollable) {
      if (this.scrollable.isNodeInView(previewElem)) {
        return;
      }
      return this.scrollable.scrollTo(previewElem, {
        align: 'bottom',
        animated: true,
        duration: 250,
      });
    }

    return previewElem.scrollIntoView();
  }

  // We want to download a transfer or single file, so request from the API
  // where we can obtain the file and then go get it!
  async onDownloadAll() {
    const { transfer, session, currentSize } = this.props;

    await Store.dispatch(
      TransferDownloadActions.downloadTransfer({
        transfer,
        secret: this.props.secret,
        recipientId: this.props.recipient || false,
        domainUserId: session.domainUserId,
        currentSize,
      })
    );
  }

  onExpandText() {
    this.setState({ textExpanded: true });
  }

  onShowAllFiles() {
    this.setState({ showAllFilesOnMobile: true });
  }

  async onFileDownload(id) {
    const { transfer, session } = this.props;

    await Store.dispatch(
      TransferDownloadActions.downloadTransferFile({
        transfer,
        secret: this.props.secret,
        recipientId: this.props.recipient || false,
        fileId: id,
        domainUserId: session.domainUserId,
      })
    );
  }

  renderDownloadIcon(transfer) {
    if (transfer.itemsTree.items.length > 2 && !this.previewable) {
      return null;
    }

    return (
      <Icon
        type={
          transfer.state === 'processing'
            ? 'TransferProcessing'
            : 'TransferReady'
        }
        className="downloader__top-icon"
      />
    );
  }

  trackOpenInAppButton = () => {
    Analytics.trackGoogleEvent({
      category: 'popup experiment',
      action: 'open in app button',
    });

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_open_in_app',
      property: 'button_download_page',
    });
  };

  trackDownloadAppButton = () => {
    Analytics.trackGoogleEvent({
      category: 'popup experiment',
      action: 'download app button',
    });

    Analytics.trackSnowplowEvent({
      category: 'transfer',
      action: 'click_download_the_app',
      property: 'button_download_page',
    });
  };

  onTransferLinkCopied = () => {
    const { transferLinkButtonTimerId } = this.state;

    if (transferLinkButtonTimerId) {
      clearTimeout(transferLinkButtonTimerId);
    }

    const timerId = setTimeout(() => {
      this.setState({
        transferLinkButtonTimerId: null,
        transferLinkButtonLabel: I18n.t('transfer_link.buttons.copy_again'),
      });
    }, 4000);

    this.setState({
      transferLinkButtonTimerId: timerId,
      transferLinkButtonLabel: I18n.t('transfer_link.buttons.copied'),
    });
  };

  // Render our view
  render() {
    const { transfer, route, currentSize } = this.props;
    const { transferLinkButtonLabel } = this.state;
    const totalItemsCount = transfer.items.length;
    const topLevelItemsCount = transfer.itemsTree.items.length;
    const buttonClassNames = ['transfer__button'];
    const isGrid = this.previewable;
    const hasFiles = transfer.items.some(
      ({ content_identifier }) => content_identifier === 'file'
    );
    let isActiveButton = true;
    let downloadLabel = 'download';

    if (transfer.currentFileDownload >= 0 || !this.props.isReady || !hasFiles) {
      isActiveButton = false;
      buttonClassNames.push('transfer__button--inactive');
    }

    // How long before the transfer expires?
    let description = I18n.t('transfer.download.expires', {
      relative_time: I18n.relativeTime(transfer.expiryInSeconds),
    });

    if (typeof transfer.expiryInSeconds === 'undefined') {
      description = I18n.t('transfer.download.expires_never');
    }

    const headingClassNames = ['downloader__heading'];
    if (!this.previewable) {
      headingClassNames.push(
        `downloader__heading--${topLevelItemsCount}-files`
      );
    } else {
      headingClassNames.push('downloader__heading--grid-available');
    }

    let message = null;

    if (transfer.message) {
      let expandButton = null;
      const messageClassNames = ['downloader__message'];
      let textMessage = transfer.message;
      headingClassNames.push('downloader__heading--message');

      // Cut it off.
      if (
        transfer.message.length < MESSAGE_CUTOFF_POINT ||
        this.state.textExpanded
      ) {
        messageClassNames.push('downloader__message--expanded');
      } else if (transfer.message.length >= MESSAGE_CUTOFF_POINT) {
        textMessage = `${textMessage.substr(0, MESSAGE_CUTOFF_POINT)} ...`;
        expandButton = (
          <span className="downloader__expand-msg" onClick={this.onExpandText}>
            {I18n.t('transfer.download.expand')}
            <Icon type="Arrow" orientation="bottom" />
          </span>
        );
      }

      message = (
        <div className={messageClassNames.join(' ')}>
          <p>
            {textMessage}
            {expandButton}
          </p>
        </div>
      );
    }

    const title = this.props.isReady
      ? I18n.t('transfer.download.title')
      : I18n.t('transfer.pending.title');

    let itemList = null;

    const initialItemsNumber =
      AppConfig.layout.lists.initialItemsNumber.downloader;

    if (isGrid) {
      itemList = (
        <GridButton
          isPanelOpen={
            route.name === 'grid-panel' || route.name === 'preview-panel'
          }
          ready={this.props.isReady}
          transfer={transfer}
          currentSize={currentSize}
        />
      );
    } else {
      itemList = (
        <ItemList
          transfer={transfer}
          altText={I18n.t('transfer.pending.subtitle')}
          showPending={!this.props.isReady}
          maxFiles={
            !Browser.isMobile || this.state.showAllFilesOnMobile
              ? 0
              : initialItemsNumber
          }
          previewable={
            transfer.items.find((item) => item.previewable === true) !==
            undefined
          }
          onDownload={this.onFileDownload}
          id={this.props.id}
          secret={this.props.secret}
          recipient={this.props.recipient}
          file_id={this.props.file_id}
        />
      );
    }

    let content = (
      <Scrollable
        ref={(node) => (this.scrollable = node)}
        className="transfer__contents"
      >
        <div className={headingClassNames.join(' ')}>
          {this.renderDownloadIcon(transfer)}
          <h2>{title}</h2>
          <p>{description}</p>
          {message}
        </div>
        {itemList}
      </Scrollable>
    );

    if (Browser.isMobile) {
      if (totalItemsCount > 1) {
        downloadLabel = 'download_all_files';
      }

      content = [
        <div key="render_download" className={headingClassNames.join(' ')}>
          <UpsellCollectHeader shortUrl={transfer.shortURL} />
          {this.renderDownloadIcon(transfer)}
          <h2>{title}</h2>
          <p>{description}</p>
          {message}
        </div>,
      ];

      if (isGrid) {
        content.push(<Grid key="grid" route={route} />);
      } else {
        content.push(
          <h3
            key="downloader__file-count-title"
            className="downloader__file-count-title"
          >
            {I18n.t('transfer.download.files_nosize', {
              count: totalItemsCount,
            })}
          </h3>
        );
        content.push(itemList);
        if (
          !this.state.showAllFilesOnMobile &&
          topLevelItemsCount > initialItemsNumber
        ) {
          content.push(
            <span
              key="downloader__show-all-files"
              className="downloader__show-all-files"
              onClick={this.onShowAllFiles}
            >
              {I18n.t('transfer.download.show_all_files_mobile')}
            </span>
          );
        }
      }

      const downloadButtonLabel =
        totalItemsCount === 1 ? 'download_file' : 'download_zip';
      buttonClassNames.push('transfer__button--secondary');

      content.push(
        <button
          key="transfer_footer_action"
          tabIndex={this.props.acceptedTandC ? null : '-1'}
          onClick={this.onDownloadAll}
          className={buttonClassNames.join(' ')}
          disabled={!isActiveButton}
        >
          {I18n.t(`transfer.buttons.${downloadButtonLabel}`)}
        </button>
      );

      if (transfer.shortURL) {
        content.push(
          <TransferLink
            key="transfer-link"
            label={I18n.t('transfer_link.labels.share')}
            buttonLabel={transferLinkButtonLabel}
            notificationPosition={NotificationPositions.none}
            className="u-margin-top u-margin-bottom"
            queryParams={{ src: TransferLinkSources.downloads }}
            onLinkCopied={this.onTransferLinkCopied}
          />
        );
      }
    }

    const footerContents = [];

    if (!Browser.isMobile) {
      footerContents.push(
        <button
          key="transfer_footer_action"
          tabIndex={this.props.acceptedTandC ? null : '-1'}
          onClick={this.onDownloadAll}
          className={buttonClassNames.join(' ')}
          disabled={!isActiveButton}
        >
          {I18n.t(`transfer.buttons.${downloadLabel}`)}
        </button>
      );
    }

    if (Browser.isMobile && totalItemsCount > 1) {
      footerContents.push(
        <p
          key="zip_information"
          dangerouslySetInnerHTML={{
            __html: I18n.t('transfer.download.zip_information'),
          }}
        />
      );
    }

    const classNames = ['transfer__window', 'downloader'];
    const footerClassNames = ['transfer__footer'];

    // TODO: Info icon based on X hours remaining
    return (
      <React.Fragment>
        <div
          ref={(node) => (this.node = node)}
          className={classNames.join(' ')}
        >
          {content}
          <div className={footerClassNames.join(' ')}>{footerContents}</div>
        </div>
      </React.Fragment>
    );
  }
}

Files.propTypes = {
  transfer: PropTypes.object,
  id: PropTypes.string,
  secret: PropTypes.string,
  recipient: PropTypes.any,
  file_id: PropTypes.string,
  isReady: PropTypes.bool,
  route: PropTypes.object.isRequired,
  acceptedTandC: PropTypes.bool,
  session: PropTypes.object.isRequired,
  currentSize: PropTypes.number,
};

const mapStateToProps = (state) => ({
  route: state.route,
  session: state.session,
  transfer: state.transfer,
  currentSize: getCurrentSize(state),
  acceptedTandC: state.session.tandc,
});

export default connect(mapStateToProps)(Files);
